import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CategorySidebar } from './CategorySidebar';
import { CategoryView } from './CategoryView';
import { CategoryCreateModal } from './CategoryCreateModal';
import { grammarService } from '../../services/grammarService';
import { Notification } from '../Notification/Notification';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

// Using the types from your existing implementation
interface GrammarCategory {
  name: string;
  display_name: string;
  description?: string;
  metadata_exists?: boolean;
  metadata_pending?: boolean;
  linguistic_type?: string;
  functional_purpose?: string[];
  complexity_range?: {
    min?: number;
    max?: number;
    average?: number;
  };
  cefr_coverage?: {
    introduction_range?: string[];
    mastery_range?: string[];
    core_level?: string;
  };
  subcategory_count?: number;
  common_challenges?: {
    issue: string;
    description: string;
  }[];
  business_relevance?: {
    importance?: string;
    key_contexts?: string[];
    example_use_case?: string;
  };
  teaching_approach?: {
    recommended_method?: string;
    key_concepts?: string[];
    practice_focus?: string[];
  };
  features?: any[]; // Will be populated when category is selected
  featureCount?: number;
}

interface DatabaseEditorProps {
  onError: (error: string | null) => void;
}

export function DatabaseEditor({ onError }: DatabaseEditorProps) {
  const [selectedCategory, setSelectedCategory] = useState<GrammarCategory | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState<GrammarCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState<any[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    categoryName: string;
    featureCount: number;
  }>({ isOpen: false, categoryName: '', featureCount: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchFeatures(selectedCategory.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory?.name]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      onError(null);
      
      // Fetch categories from single endpoint
      let categoriesData = await grammarService.getCategories();
      
      // Process categories and ensure display_name exists
      categoriesData = categoriesData.map(cat => ({
        ...cat,
        display_name: cat.display_name || cat.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
        metadata_exists: !!(cat.complexity_range || cat.business_relevance || cat.cefr_coverage)
      }));
      
      // Keep the featureCount from backend, it's already correctly set
      
      setCategories(categoriesData);
      if (categoriesData.length > 0 && !selectedCategory) {
        setSelectedCategory(categoriesData[0]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      onError(error instanceof Error ? error.message : 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatures = async (categoryName: string) => {
    try {
      setLoadingFeatures(true);
      const result = await grammarService.getFeaturesByCategory(categoryName);
      setFeatures(result.features);
    } catch (error) {
      console.error('Error fetching features:', error);
      onError(error instanceof Error ? error.message : 'Failed to fetch features');
    } finally {
      setLoadingFeatures(false);
    }
  };

  const filteredCategories = categories.filter(category =>
    category.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCategorySelect = (category: GrammarCategory) => {
    setSelectedCategory(category);
  };

  const handleCategoryEdit = (category: GrammarCategory) => {
    // This will open the category editor
    console.log('Edit category:', category.name);
    // TODO: Implement category edit modal
  };

  const handleCategoryDelete = async (categoryName: string) => {
    // First check if category has features
    const category = categories.find(c => c.name === categoryName);
    const featureCount = category?.featureCount || category?.features?.length || 0;
    
    if (featureCount > 0) {
      setNotification({
        type: 'error',
        message: `Cannot delete category "${category?.display_name}". Please remove all ${featureCount} features first.`
      });
      return;
    }
    
    // Show delete confirmation dialog
    setDeleteDialog({
      isOpen: true,
      categoryName: categoryName,
      featureCount: featureCount
    });
  };

  const confirmCategoryDelete = async () => {
    try {
      await grammarService.deleteCategory(deleteDialog.categoryName);
      
      // Remove category from local state
      setCategories(prev => prev.filter(c => c.name !== deleteDialog.categoryName));
      
      // Clear selection if deleting current category
      if (selectedCategory?.name === deleteDialog.categoryName) {
        setSelectedCategory(null);
        setFeatures([]);
      }
      
      setNotification({
        type: 'success',
        message: 'Category deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      setNotification({
        type: 'error',
        message: 'Failed to delete category'
      });
    } finally {
      setDeleteDialog({ isOpen: false, categoryName: '', featureCount: 0 });
    }
  };

  const handleFeatureEdit = (feature: any) => {
    // TODO: Implement feature edit modal
    console.log('Edit feature:', feature.feature_id);
  };

  const handleFeatureDelete = async (featureId: string) => {
    try {
      await grammarService.deleteFeature(featureId);
      
      // Refresh features
      if (selectedCategory) {
        await fetchFeatures(selectedCategory.name);
      }
      
      setNotification({
        type: 'success',
        message: 'Feature deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting feature:', error);
      setNotification({
        type: 'error',
        message: 'Failed to delete feature'
      });
    }
  };

  const handleAddCategory = () => {
    setShowCreateModal(true);
  };

  const handleCreateCategory = async (categoryData: any) => {
    try {
      setSavingCategory(true);
      const result = await grammarService.createCategory(categoryData);
      
      // Close modal and refresh categories
      setShowCreateModal(false);
      await fetchCategories();
      
      setNotification({
        type: 'success',
        message: result.message || 'Category created successfully'
      });
    } catch (error) {
      console.error('Error creating category:', error);
      setNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to create category'
      });
    } finally {
      setSavingCategory(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-background overflow-hidden">
      {/* Sidebar */}
      <div className="w-80 border-r border-border bg-sidebar flex flex-col h-full overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2 mb-4">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={handleAddCategory}>
            <Plus className="w-4 h-4 mr-2" />
            Create Category
          </Button>
        </div>
        <CategorySidebar
          categories={filteredCategories}
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
          onCategoryEdit={handleCategoryEdit}
          onCategoryDelete={handleCategoryDelete}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedCategory ? (
          <CategoryView
            category={selectedCategory}
            features={features}
            loadingFeatures={loadingFeatures}
            onFeatureEdit={handleFeatureEdit}
            onFeatureDelete={handleFeatureDelete}
            onCategoryUpdate={fetchCategories}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <h3>No category selected</h3>
              <p>Select a category from the sidebar to view its details and features.</p>
            </div>
          </div>
        )}
      </div>

      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.isOpen} onOpenChange={(open) => !open && setDeleteDialog({ isOpen: false, categoryName: '', featureCount: 0 })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the category "{categories.find(c => c.name === deleteDialog.categoryName)?.display_name}"? 
              This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ isOpen: false, categoryName: '', featureCount: 0 })}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmCategoryDelete}
            >
              Delete Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Create Modal */}
      <CategoryCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateCategory}
        saving={savingCategory}
      />
    </div>
  );
}